import { useFormContext, useWatch } from 'react-hook-form';
import { GeoEventFormStatus } from 'shared/types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { Flooding } from 'features/flooding/flooding.types';
import { Hazard } from 'features/hazards/hazard.types';
import { ReportableGeoEvent } from 'features/reports/components/ReportForm/types';
import {
  isFloodingGeoEvent,
  isHazardGeoEvent,
  isWildfireGeoEvent,
} from 'features/geoEvents/geoEvent.utils';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { NotificationType } from 'features/notifications/notifications.types';
import { DEFAULT_LAT, DEFAULT_LON } from '../../constants';

type UseMapGeoEventProps<T> = {
  geoEvent: T;
  geoEventType?: GeoEventType;
};

type OptionalGeoEvent<T> = T extends Wildfire
  ? Wildfire
  : T extends Flooding
    ? Flooding
    : T extends Hazard
      ? Hazard
      : Partial<ReportableGeoEvent>;

const createWildfireGeoEventForMapMarker = <T extends Wildfire | undefined>(
  geoEvent: T,
  status: GeoEventFormStatus,
  acreage: number | null,
  containment: number | null,
  isFps: boolean,
  isPrescribed: boolean,
  isReportUpdate: boolean,
  geoEventLat: string,
  geoEventLng: string,
  manualDeactivationStarted: boolean | null,
  notificationType: NotificationType,
): OptionalGeoEvent<T> => {
  return {
    ...(geoEvent || {}),
    isActive: status === 'active',
    notificationType,
    data: {
      geoEventType: 'wildfire',
      evacuationOrders: '',
      evacuationWarnings: '',
      evacuationNotes: '',
      evacuationAdvisories: '',
      evacuationShelterInPlace: '',
      links: [],
      ...(geoEvent ? geoEvent.data : {}),
      acreage,
      containment,
      isFps,
      isPrescribed,
      manualDeactivationStarted,
    },
    ...(isReportUpdate
      ? {}
      : {
          lat: Number(geoEventLat),
          lng: Number(geoEventLng),
        }),
  } as OptionalGeoEvent<T>;
};

const createFloodingGeoEventForMapMarker = <T extends Flooding | undefined>(
  geoEvent: T,
  status: GeoEventFormStatus,
  isReportUpdate: boolean,
  geoEventLat: string,
  geoEventLng: string,
  notificationType: NotificationType,
): OptionalGeoEvent<T> => {
  return {
    ...(geoEvent || {}),
    isActive: status === 'active',
    notificationType,
    data: {
      geoEventType: 'flooding',
      ...(geoEvent ? geoEvent.data : {}),
    },
    ...(isReportUpdate
      ? {}
      : {
          lat: Number(geoEventLat),
          lng: Number(geoEventLng),
        }),
  } as OptionalGeoEvent<T>;
};

const createHazardGeoEventForMapMarker = <T extends Hazard | undefined>(
  geoEvent: T,
  status: GeoEventFormStatus,
  isReportUpdate: boolean,
  geoEventLat: string,
  geoEventLng: string,
  notificationType: NotificationType,
): OptionalGeoEvent<T> => {
  return {
    ...(geoEvent || {}),
    isActive: status === 'active',
    notificationType,
    data: {
      geoEventType: 'hazard',
      ...(geoEvent ? geoEvent.data : {}),
    },
    ...(isReportUpdate
      ? {}
      : {
          lat: Number(geoEventLat),
          lng: Number(geoEventLng),
        }),
  } as OptionalGeoEvent<T>;
};

const useCommonGeoEventFields = (
  geoEvent: Wildfire | Flooding | Hazard | undefined,
  isReportUpdate: boolean,
): {
  status: GeoEventFormStatus;
  geoEventLat: string;
  geoEventLng: string;
  notificationType: NotificationType;
} => {
  const { control } = useFormContext();

  const status: GeoEventFormStatus = useWatch({
    control,
    name: 'status',
    defaultValue: geoEvent?.isActive ? 'active' : 'inactive',
  });
  const geoEventLat: string = useWatch({
    control,
    name: 'lat',
    defaultValue: geoEvent?.lat ?? DEFAULT_LAT.toString(),
    disabled: isReportUpdate,
  });
  const geoEventLng: string = useWatch({
    control,
    name: 'lng',
    defaultValue: geoEvent?.lng ?? DEFAULT_LON.toString(),
    disabled: isReportUpdate,
  });
  const watchedNotificationType: NotificationType = useWatch({
    control,
    name: 'notificationType',
    defaultValue: geoEvent?.notificationType ?? NotificationType.Silent,
    disabled: isReportUpdate,
  });
  const notificationType = isReportUpdate
    ? geoEvent!.notificationType
    : watchedNotificationType;

  return { status, geoEventLat, geoEventLng, notificationType };
};

export const useFormMapGeoEvent = <
  T extends Wildfire | Flooding | Hazard | undefined,
>(
  props: UseMapGeoEventProps<T>,
): OptionalGeoEvent<T> => {
  const { geoEvent, geoEventType } = props;
  const { control } = useFormContext();
  const isReportUpdate = Boolean(geoEvent);
  const isWildfire =
    (geoEvent && isWildfireGeoEvent(geoEvent)) || geoEventType === 'wildfire';
  const isFlooding =
    (geoEvent && isFloodingGeoEvent(geoEvent)) || geoEventType === 'flooding';
  const isHazard =
    (geoEvent && isHazardGeoEvent(geoEvent)) || geoEventType === 'hazard';

  // Common fields for all event types
  const { status, geoEventLat, geoEventLng, notificationType } =
    useCommonGeoEventFields(geoEvent, isReportUpdate);

  // Wildfire-specific fields - only subscribe when it's actually a wildfire
  const wildfireGeoEvent = isWildfire ? (geoEvent as Wildfire) : undefined;
  const containment: number | null = useWatch({
    control,
    name: 'containment',
    defaultValue: wildfireGeoEvent?.data.containment ?? null,
    disabled: !isWildfire,
  });
  const acreage: number | null = useWatch({
    control,
    name: 'acreage',
    defaultValue: wildfireGeoEvent?.data.acreage ?? null,
    disabled: !isWildfire,
  });
  const isFps: boolean = useWatch({
    control,
    name: 'isFps',
    defaultValue: Boolean(wildfireGeoEvent?.data.isFps),
    disabled: !isWildfire,
  });
  const isPrescribed: boolean = useWatch({
    control,
    name: 'isPrescribed',
    defaultValue: Boolean(wildfireGeoEvent?.data.isPrescribed),
    disabled: !isWildfire || isReportUpdate,
  });
  const manualDeactivationStarted: boolean | null = useWatch({
    control,
    name: 'manualDeactivationStarted',
    defaultValue: wildfireGeoEvent?.data.manualDeactivationStarted || null,
    disabled: !isWildfire,
  });

  if (isWildfire) {
    return createWildfireGeoEventForMapMarker(
      geoEvent as Wildfire | undefined,
      status,
      acreage,
      containment,
      isFps,
      isPrescribed,
      isReportUpdate,
      geoEventLat,
      geoEventLng,
      manualDeactivationStarted,
      notificationType,
    ) as OptionalGeoEvent<T>;
  }
  if (isFlooding) {
    return createFloodingGeoEventForMapMarker(
      geoEvent as Flooding | undefined,
      status,
      isReportUpdate,
      geoEventLat,
      geoEventLng,
      notificationType,
    ) as OptionalGeoEvent<T>;
  }
  if (isHazard) {
    return createHazardGeoEventForMapMarker(
      geoEvent as Hazard | undefined,
      status,
      isReportUpdate,
      geoEventLat,
      geoEventLng,
      notificationType,
    ) as OptionalGeoEvent<T>;
  }
  // For undefined geoEvent, return minimal structure
  return {
    isActive: status === 'active',
    lat: Number(geoEventLat),
    lng: Number(geoEventLng),
  } as OptionalGeoEvent<T>;
};
